export type AdminSettingKey =
  | 'site_url'
  | 'mollie_api_key'
  | 'postmark_server_id'
  | 'postmark_server_token'
  | 'postmark_from_email'
  | 'postmark_reply_to'
  | 'postmark_admin_to'
  | 'postmark_default_message_stream';

export interface AdminSettingView {
  key: AdminSettingKey;
  category: 'payment' | 'email' | 'general';
  label: string;
  description: string;
  placeholder: string;
  sensitive: boolean;
  configured: boolean;
  value: string;
  maskedValue: string;
  source: 'environment' | 'database' | 'default';
  updatedAt: string;
  lastTestStatus: string;
  lastTestMessage: string;
  lastTestedAt: string;
}

export interface AdminSettingsSnapshot {
  infrastructure: {
    supabaseConfigured: boolean;
    serviceRoleConfigured: boolean;
    settingsSecretConfigured: boolean;
  };
  providers: {
    mollie: {
      configured: boolean;
      siteUrlConfigured: boolean;
      lastTestStatus: string;
      lastTestMessage: string;
      lastTestedAt: string;
    };
    postmark: {
      configured: boolean;
      lastTestStatus: string;
      lastTestMessage: string;
      lastTestedAt: string;
    };
  };
  settings: Record<AdminSettingKey, AdminSettingView>;
}

const parseResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Erreur admin settings');
  }

  return payload as T;
};

export async function getAdminSettingsSnapshot(): Promise<AdminSettingsSnapshot> {
  const response = await fetch('/adminSettings.php', {
    credentials: 'include',
    cache: 'no-store',
  });

  return parseResponse<AdminSettingsSnapshot>(response);
}

export async function saveAdminSettings(
  settings: Partial<Record<AdminSettingKey, string>>
): Promise<AdminSettingsSnapshot> {
  const response = await fetch('/adminSettings.php', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ settings }),
  });

  const payload = await parseResponse<{ ok: true; snapshot: AdminSettingsSnapshot }>(response);
  return payload.snapshot;
}

export async function testProviderConnection(
  provider: 'mollie' | 'postmark'
): Promise<{ ok: true; provider: string; message: string }> {
  const response = await fetch('/adminSettingsTest.php', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ provider }),
  });

  return parseResponse<{ ok: true; provider: string; message: string }>(response);
}

export const adminSettingsService = {
  getAdminSettingsSnapshot,
  saveAdminSettings,
  testProviderConnection,
};
